/**
 * useMentionCandidates — builds the agent autocomplete list for
 * `@mention` routing in Ask Argus chat (feat/multi-agent-in-chat).
 *
 * Founder spec: the dropdown shows only agents attached to THIS
 * conversation (via `conversation.attached_tools`). For the initial
 * PR `argus-code` is the only working specialist; it's shipped as a
 * built-in `system.argus_code` agent that the new-conversation
 * resolver attaches by default (see `attached-tools-resolver` backend
 * service).
 *
 * Data sources:
 *
 *   1. GET /custom-agents             → tenant + system agents
 *      (the same query the existing model/agent dropdown uses).
 *
 *   2. GET /conversations/:id/attached-tools (optional)
 *      → the per-conversation override list. Provided by parent — we
 *      don't fire the query here, we filter against it.
 *
 * Why a hook + not a server fetch on every keystroke: typing latency
 * matters. The candidate set is small enough (≤ tens) that filtering
 * client-side is essentially free and the autocomplete feels native.
 */

import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { apiClient } from '@/lib/api-client';
import type { MentionCandidate } from './mention-menu';

/**
 * Subset of the /custom-agents response that we need.
 *
 * Note: `custom_agents` is a view that surfaces both tenant agents
 * (org_id = tenant) and system agents (org_id IS NULL with
 * `visibleToTenant = true`) — the API merges them, so this single
 * query gives us everything addressable from chat.
 */
interface AgentRow {
  id: string;
  slug?: string;
  name: string;
  description: string | null;
  icon?: string | null;
  kind?: string | null;
  /** When true, the agent participates in @mention routing. */
  isSystem?: boolean;
}

/**
 * Attached-tools wire shape (matches AttachedToolItem on the backend).
 */
export interface AttachedToolItem {
  id: string;
  kind: 'connector' | 'agent' | 'skill' | 'builtin';
  enabled: boolean;
}

/**
 * Default candidates we hard-code when the agents list is loading.
 * Ensures `@argus-code` is always discoverable even before the agents
 * query lands.
 */
const FALLBACK_CANDIDATES: MentionCandidate[] = [
  {
    mentionSlug: 'argus-code',
    dbSlug: 'system.argus_code',
    displayName: 'Argus Code',
    description: 'Code specialist (Qwen 2.5 Coder 32B). Generation, review, debugging.',
    kind: 'specialist',
  },
];

/**
 * Normalize an agent row into a MentionCandidate. Defaults the
 * mention slug to a kebab-case derivative of the agent's name so even
 * tenant-custom agents are mentionable.
 *
 * For system agents (slug pattern `system.<name>`), the user-facing
 * mention slug is `<name>` with underscores → hyphens.
 */
function rowToCandidate(row: AgentRow): MentionCandidate {
  const dbSlug = row.slug ?? row.id;
  // Derive a user-typed mention slug from the DB slug.
  // `system.argus_code` → `argus-code`
  // `custom.maintenance_ops` → `maintenance-ops`
  // `acme-finance` → `acme-finance` (already kebab)
  const trimmed = dbSlug.replace(/^system\./, '').replace(/^custom\./, '');
  const mentionSlug = trimmed.replace(/_/g, '-');

  return {
    mentionSlug,
    dbSlug,
    // bug #24 — some agents (QA artifacts) have a blank name; fall back to the
    // mention slug so the picker never renders a nameless row.
    displayName: row.name?.trim() || mentionSlug,
    description: row.description ?? '',
    kind: row.kind ?? 'specialist',
  };
}

/**
 * Build the filtered candidate list for the current partial mention.
 *
 * @param partial    The string typed after `@` (lowercased).
 * @param attached   Per-conversation attached_tools list (null = no gate).
 * @returns          Loading state + filtered candidate list.
 */
export function useMentionCandidates(
  partial: string,
  attached: AttachedToolItem[] | null,
): { loading: boolean; candidates: MentionCandidate[] } {
  const agentsQuery = useQuery<{ data: AgentRow[] }>({
    queryKey: ['custom-agents', { view: 'all' }],
    queryFn: () => apiClient.get<{ data: AgentRow[] }>('/custom-agents?view=all'),
    // Cache aggressively — the agent list rarely changes mid-session.
    staleTime: 60_000,
  });

  const candidates = useMemo(() => {
    const rows = agentsQuery.data?.data ?? [];
    // Build the raw list. Fall back to FALLBACK_CANDIDATES when the
    // query is loading so `@argus-code` is always discoverable.
    let all: MentionCandidate[] = rows.length > 0
      ? rows.map(rowToCandidate)
      : FALLBACK_CANDIDATES;

    // Ensure system.argus_code is always present (it's the canonical
    // specialist this PR ships). If the agents endpoint doesn't
    // include it yet (seed not run, etc.), prepend the fallback.
    if (!all.some((c) => c.dbSlug === 'system.argus_code')) {
      all = [...FALLBACK_CANDIDATES, ...all];
    }

    // Apply per-conversation attached_tools gate. Founder spec: only
    // agents attached to this chat appear in the autocomplete.
    if (attached !== null) {
      const allowedDbSlugs = new Set(
        attached
          .filter((a) => a.kind === 'agent' && a.enabled)
          .map((a) =>
            a.id.startsWith('agent:') ? a.id.slice('agent:'.length) : a.id,
          ),
      );
      // Ask Argus itself is implicitly always available — don't filter
      // it out even when not in attached_tools.
      all = all.filter(
        (c) =>
          c.dbSlug === 'system.ask_argus' ||
          c.dbSlug === 'ask-argus' ||
          c.kind === 'ask_argus' ||
          allowedDbSlugs.has(c.dbSlug),
      );
    }

    // Filter by partial match. Empty partial = show all (up to cap).
    const needle = partial.toLowerCase();
    const filtered = needle.length === 0
      ? all
      : all.filter(
          (c) =>
            c.mentionSlug.toLowerCase().includes(needle) ||
            c.displayName.toLowerCase().includes(needle) ||
            c.dbSlug.toLowerCase().includes(needle),
        );

    // Sort: exact-prefix matches first, then everything else
    // alphabetical. This puts `@arg` → `argus-code` ahead of
    // `@arg` → `Architect-of-things` etc.
    filtered.sort((a, b) => {
      const aStarts = a.mentionSlug.toLowerCase().startsWith(needle);
      const bStarts = b.mentionSlug.toLowerCase().startsWith(needle);
      if (aStarts !== bStarts) return aStarts ? -1 : 1;
      return a.displayName.localeCompare(b.displayName);
    });

    // Hard cap on render — we don't want the dropdown to scroll for
    // 200 agents. 8 fits comfortably above the composer.
    return filtered.slice(0, 8);
  }, [agentsQuery.data, partial, attached]);

  return {
    loading: agentsQuery.isLoading,
    candidates,
  };
}
